import Service, { service } from '@ember/service';
import config from 'garaje/config/environment';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import urlBuilder from 'garaje/utils/url-builder';
import { cached } from 'tracked-toolbox';

export interface SupportedPlugin {
  id: string;
  name: string;
}

interface SupportedPluginsResponse {
  plugins: SupportedPlugin[];
}

export default class DiplomatPluginsService extends Service {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  @cached
  get supportedPlugins(): Promise<SupportedPlugin[]> {
    return this.fetchSupportedPlugins();
  }

  /**
   * Determines which diplomat server host to use based on pairing flow
   * Pairing flow enabled → use latest server (v2)
   * Pairing flow disabled → use v1 server via dashboard proxy
   */
  getDiplomatServerHost(): string {
    const isPairingFlowEnabled = this.featureFlags.isEnabled('diplomat-client-pairing-flow');
    // TEST: Use dashboard proxy URL for v1 to test if origin rule alone is sufficient
    return isPairingFlowEnabled ? config.diplomatServerHost : `${config.host}/a/diplomat-server-v1`;
  }

  private async fetchSupportedPlugins(): Promise<SupportedPlugin[]> {
    try {
      const host = this.getDiplomatServerHost();
      const url = urlBuilder.diplomatServer.supportedPluginsUrl(host);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.warn('Failed to fetch supported plugins from diplomat-server, falling back to defaults');

        this.workplaceMetrics.trackEvent('DIPLOMAT_PLUGINS_FETCH_FAILED', {
          status: response.status,
          status_text: response.statusText,
          url,
          timestamp: new Date().toISOString(),
        });

        return this.getFallbackPlugins();
      }

      const data = (await response.json()) as SupportedPluginsResponse;
      return data.plugins || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Error fetching supported plugins from diplomat-server, falling back to defaults');

      const host = this.getDiplomatServerHost();
      this.workplaceMetrics.trackEvent('DIPLOMAT_PLUGINS_FETCH_ERROR', {
        error_message: error instanceof Error ? error.message : String(error),
        url: urlBuilder.diplomatServer.supportedPluginsUrl(host),
        timestamp: new Date().toISOString(),
      });

      return this.getFallbackPlugins();
    }
  }

  private getFallbackPlugins(): SupportedPlugin[] {
    // Fallback to hardcoded list if API is unavailable
    // Return environment-specific plugins
    const sharedPlugins = [{ id: '4c416333-bfd0-48ac-aaec-e9224559f1b5', name: 'Kantech EntraPass by Tyco' }];

    if (config.environment === 'production') {
      return [
        { id: 'f03ff630-d6be-4f0d-99d9-ecf810a0a5d3', name: 'LenelS2 Netbox' },
        { id: 'a38bce29-2b86-47d7-9f34-8a2184233c2f', name: 'C-CURE 9000' },
        ...sharedPlugins,
      ];
    } else {
      // Staging/development environments
      return [
        { id: '7a350fe7-dabb-4d22-9471-2054b5e041da', name: 'LenelS2 Netbox (Staging)' },
        { id: 'b952c5cb-6296-492a-bed7-41efe76b5c34', name: 'C-CURE 9000 (Staging)' },
        ...sharedPlugins,
      ];
    }
  }
}
